import { Input, ScrollShadow } from "@heroui/react";
import { AnimatePresence, motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import { DrawerWrapper } from "@/comps/DrawerWrapper";
import { createNiceModal } from "@/utils/nicemodel";
import type countryConfig from "$/open_dict/country.json";
import { Icon } from "@/assets/icon/Icon";
import { I18nNamespace } from "@/const/i18nNamespace";
import { CountryList } from "@/comps/CountryList";
import { CountryListItem } from "@/comps/CountryListItem";
import noSearchResultImg from "@/assets/system/no_search.jpg";
import { useContryCodeMaps } from "@/hooks/useContryCodeMaps";

export type ConfigKey = keyof typeof countryConfig;

export const openCountryModal = createNiceModal<
  { code?: ConfigKey },
  { code: ConfigKey }
>(({ _modal, code }) => {
  const [searchValue, setSearchValue] = useState("");
  const { t } = useTranslation();

  const onChange = (c: ConfigKey) => {
    _modal.resolve({ code: c }); // 关闭模态框
  };

  const countryCodeMaps = useContryCodeMaps();

  const filterCountryCodes = useMemo(() => {
    const keyword = searchValue.trim().toLowerCase();

    if (!keyword) {
      return [];
    }
    return countryCodeMaps
      .flatMap((group) => group.value)
      .filter((_code) => {
        const label = t(`${I18nNamespace.COUNTRY}:${_code}`).toLowerCase();
        const codeHit = _code.includes(keyword);
        const textHit = label.includes(keyword);
        return codeHit || textHit;
      });
  }, [searchValue]);

  return (
    <DrawerWrapper
      {..._modal.props}
      baseClassName="h-[90vh] h-[90dvh]"
      onClose={() => {
        setSearchValue("");
      }}
    >
      <div className="flex flex-1 flex-col overflow-hidden">
        <div className="px-6 pb-2 pt-3">
          <Input
            classNames={{
              inputWrapper:
                "h-11 border border-color-border-2 bg-white data-[hover=true]:bg-white group-data-[focus=true]:bg-white group-data-[focus=true]:border-color-primary",
              input: "placeholder:text-color-text-4",
            }}
            placeholder={t("country_search_placeholder")}
            startContent={<Icon type="search" />}
            endContent={
              searchValue ? (
                <Icon type="closeFill" onClick={() => setSearchValue("")} />
              ) : null
            }
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
          />
        </div>
        <ScrollShadow className="flex min-h-0 flex-1 flex-col overflow-y-auto pb-9 pt-2">
          <AnimatePresence mode="wait">
            <motion.div
              key={searchValue ? "searchValue" : "list"}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 20, opacity: 0 }}
              transition={{ duration: 0.1 }}
              className="flex-1"
            >
              {!searchValue ? (
                <CountryList
                  data={countryCodeMaps}
                  value={code}
                  onChange={onChange}
                />
              ) : (
                <AnimatePresence mode="wait">
                  {filterCountryCodes.length > 0 ? (
                    filterCountryCodes.map((c) => (
                      <motion.div
                        key={c}
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 0, opacity: 0 }}
                        transition={{ duration: 0.1 }}
                      >
                        <CountryListItem
                          code={c}
                          searchChat={searchValue}
                          isActived={code === c}
                          onChange={onChange}
                        />
                      </motion.div>
                    ))
                  ) : (
                    <div className="flex h-full flex-col items-center justify-center gap-3">
                      <img src={noSearchResultImg} className="w-50 h-36" />
                      <div className="text-color-text-3 text-sm">
                        {t("no_search_result")}
                      </div>
                    </div>
                  )}
                </AnimatePresence>
              )}
            </motion.div>
          </AnimatePresence>
        </ScrollShadow>
      </div>
    </DrawerWrapper>
  );
});
